import React from "react";
import { Tooltip as RadixTooltip } from "radix-ui";

export type TooltipProps = {
  content: React.ReactNode;
  children: React.ReactElement;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  /** Full override for the content box's classes (e.g. a light "card" popover) — replaces the default dark bubble styling and hides the arrow. */
  contentClassName?: string;
  /** Controlled open. Pass both to suppress the tooltip while another overlay owns the pointer. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Delay before opening. Radix keeps content open while the pointer is on it, so there is no matching close delay. */
  delayDuration?: number;
  collisionPadding?: number;
};

function Tooltip({
  content,
  children,
  side = "top",
  align = "center",
  contentClassName,
  open,
  onOpenChange,
  delayDuration = 200,
  collisionPadding,
}: TooltipProps) {
  if (!content) return children;

  return (
    <RadixTooltip.Provider delayDuration={delayDuration}>
      <RadixTooltip.Root open={open} onOpenChange={onOpenChange}>
        <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            side={side}
            align={align}
            sideOffset={6}
            collisionPadding={collisionPadding}
            className={
              contentClassName ??
              "z-radixTooltip rounded-md bg-text-heading px-2.5 py-1.5 text-xs font-medium text-white shadow-dialog data-[state=delayed-open]:animate-sheet-fade-in"
            }
          >
            {content}
            {!contentClassName && (
              <RadixTooltip.Arrow className="fill-text-heading" />
            )}
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}

export default Tooltip;
